import { PropertyType } from '@devicepilot/manifest-types';
import { useResult } from '@vue/apollo-composable';
import moment from 'moment';
import { sprintf } from 'sprintf-js';
import { useNamespacedState } from 'vuex-composition-helpers';
import { naturalSort } from '@/helpers/util';
import { ACCOUNT } from '@/store/types/namespaces';
import { usePropertiesQuery } from '../apollo/generated/use';
function escapeField(field) {
    if (field.match(/^([A-z]|^\$)\w+$/))
        return field;
    return `"{${field}}"`;
}
const timeFormatter = (v) => {
    if (v === null || v === undefined)
        return v;
    if (typeof v === 'boolean')
        return v.toLocaleString();
    const m = moment(v);
    if (!m.isValid())
        return v.toLocaleString();
    return m.toDate().toLocaleString();
};
function buildFormatter(p) {
    const isTimestamp = 'isTimestamp' in p ? p.isTimestamp : false;
    if (isTimestamp)
        return timeFormatter;
    const enumerations = 'enumerations' in p ? p.enumerations || [] : [];
    const printf = 'printf' in p ? p.printf : undefined;
    const factor = 'factor' in p ? p.factor || 1 : 1;
    const formatter = (v) => {
        if (v === null || v === undefined)
            return v;
        // prefer enumerations
        const enumeration = enumerations.find((e) => e.value === v.toString());
        if (enumeration)
            return enumeration.label;
        // apply formats
        let value = v;
        if (typeof value === 'number' && !Number.isNaN(value))
            value *= factor;
        try {
            return printf ? sprintf(printf, value) : value.toLocaleString();
        }
        catch (e) {
            return value.toLocaleString();
        }
    };
    return formatter;
}
function toListableProperty(property) {
    if (property.hidden)
        return null;
    switch (property.__typename) {
        case 'PropertyCalculated': {
            return {
                alias: property.alias || property.field,
                editable: false,
                enumerations: property.enumerations || [],
                expression: property.expression,
                factor: 1,
                field: escapeField(property.field),
                format: buildFormatter(property),
                id: property.legacyId,
                isCalculated: true,
                isEnumerated: (property.enumerations || []).length > 0,
                isOnline: false,
                isTs: property.isTimestamp,
                metadata: false,
                name: property.field,
                type: property.dataType,
                $property: property,
            };
        }
        case 'PropertyMetadata':
        case 'PropertyTelemetry': {
            return {
                alias: property.alias || property.field,
                editable: property.__typename === 'PropertyMetadata',
                enumerations: property.enumerations || [],
                expression: undefined,
                factor: property.factor || 1,
                field: escapeField(property.field),
                format: buildFormatter(property),
                id: property.legacyId,
                isCalculated: false,
                isEnumerated: (property.enumerations || []).length > 0,
                isOnline: false,
                isTs: property.isTimestamp,
                metadata: property.__typename === 'PropertyMetadata',
                name: property.field,
                type: property.dataType,
                $property: property,
            };
        }
        case 'PropertyOnline': {
            return {
                alias: property.alias || property.field,
                editable: false,
                enumerations: [],
                expression: undefined,
                factor: 1,
                field: escapeField(property.field),
                format: buildFormatter(property),
                id: property.legacyId,
                isCalculated: false,
                isEnumerated: false,
                isOnline: true,
                isTs: false,
                metadata: false,
                name: property.field,
                type: PropertyType.Boolean,
                $property: property,
            };
        }
        case 'PropertySystem': {
            return {
                alias: property.alias || property.field,
                editable: false,
                enumerations: [],
                expression: undefined,
                factor: 1,
                field: escapeField(property.field),
                format: buildFormatter(property),
                id: property.field,
                isCalculated: false,
                isEnumerated: false,
                isOnline: false,
                isTs: property.isTimestamp,
                metadata: false,
                name: property.field,
                type: property.dataType,
                $property: property,
            };
        }
        default: {
            return ((p) => {
                // @ts-ignore assert never with type means __typename
                throw new Error(`Unsupported type: ${p?.__typename}`);
            })(property);
        }
    }
}
function toTimestampListableProperty(property) {
    if (property.hidden
        || property.__typename === 'PropertyCalculated'
        || property.__typename === 'PropertyOnline'
        || (property.__typename === 'PropertySystem' && property.field !== '$id')
        || (property.__typename === 'PropertyMetadata' && !property.showTimestamp)
        || (property.__typename === 'PropertyTelemetry' && !property.showTimestamp)) {
        return null;
    }
    const alias = property.field === '$id'
        ? 'First seen'
        : `${property.alias || property.field} (last set)`;
    const id = property.__typename === 'PropertySystem'
        ? `$ts/${property.field}`
        : `$ts/${property.legacyId}`;
    const name = `$ts/${property.field}`;
    const $property = {
        __typename: 'PropertySystem',
        alias,
        dataType: PropertyType.Number,
        field: name,
        hidden: false,
        isTimestamp: true,
    };
    return {
        alias,
        editable: false,
        enumerations: [],
        factor: 1,
        field: escapeField(`$ts/${property.field}`),
        format: timeFormatter,
        id,
        isCalculated: false,
        isEnumerated: false,
        isOnline: false,
        isTs: true,
        metadata: false,
        name,
        type: PropertyType.Number,
        expression: undefined,
        $property,
    };
}
const sort = (properties) => naturalSort(properties, (p) => p.alias || p.name, undefined);
export function propertiesToListableProperties(data) {
    if (!data || !data.node || data.node.__typename !== 'Account')
        return [];
    const properties = data.node.properties
        .map(toListableProperty)
        .filter((p) => p !== null);
    const timestamps = data.node.properties
        .map(toTimestampListableProperty)
        .filter((p) => p !== null);
    // system properties ($id and 'Last seen') should appear be first
    const systemProperties = sort(properties.filter((p) => p.$property.__typename === 'PropertySystem'));
    const otherProperties = sort([
        ...properties.filter((p) => p.$property.__typename !== 'PropertySystem'),
        ...timestamps,
    ]);
    return [...systemProperties, ...otherProperties];
}
export function useListableProperties() {
    const { id } = useNamespacedState(ACCOUNT, ['id']);
    const { result } = usePropertiesQuery({ accountId: id.value }, {
        pollInterval: 5 * 60 * 1000,
        fetchPolicy: 'cache-first',
    });
    const listableProperties = useResult(result, [], propertiesToListableProperties);
    return {
        listableProperties,
    };
}
